import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import CustomButton from '../ui/Button';
import DropdownButton from '../ui/DropdownButton';
import { useBlog } from '../hooks/UseBlog';
import Loader from '../ui/Loader';
import ErrorPage from '../not_found/NotFound';
import { useTranslation } from "react-i18next";

const BlogSection = () => {
  const { t } = useTranslation("not_found");

  const { blogData, loading, error, loadMoreBlogs, hasMore } = useBlog();
  const [showAllBlogs, setShowAllBlogs] = useState(false); // State to control All Blogs section

  if (loading) return <Loader />;
  if (error) return <ErrorPage title={t("blogs_not_found_title")} description={t("service_not_found_description")} />;

  return (
    <>
      {/* Hero Section */}
      <div className='mb-16'>
        <div className="relative md:h-[592px]">
          {/* Background Image */}
          <img src="/assets/servicesbg.svg" className="w-full h-full object-cover" alt="background animation" />
          <div className="absolute inset-0 bg-[#00000099]"></div>
          <img src="/assets/ellipse.svg" className="absolute bottom-44 left-1/2 transform -translate-x-1/2 translate-y-1/2" alt="ellipse decoration" />

          {/* Hero Content */}
          <div className="absolute inset-0 px-4 md:px-8 py-20">
            <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row px-4 sm:px-8 md:px-6 gap-6">
              {/* Left Large Card */}
              <div className="md:basis-[60%]">
                {blogData[0] && (
                  <div
                    className="h-[400px] md:h-[570px] bg-cover bg-center bg-no-repeat overflow-hidden relative"
                    style={{ backgroundImage: `url('${blogData[0].images[0]?.image}')` }}
                  >
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <Link to={`/blog/${blogData[0].slug}`}>
                        <div>
                          <h2 className="body-xl-3 text-[#FFFFFF] mb-2">{blogData[0].title}</h2>
                          <p className="title-small-5 text-[#FFFFFF] mb-4">
                            {blogData[0].short_description}
                          </p>
                        </div>
                      </Link>
                      <a href="contact_us">
                        <CustomButton>{t("start_trail")}</CustomButton>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Cards */}
              <div className="md:basis-[40%] grid grid-cols-1 md:grid-cols-2 gap-4">
                {blogData.slice(1, 3).map((blog, index) => (
                  <div
                    key={index}
                    className="h-[200px] md:h-[278px] bg-cover bg-center bg-no-repeat overflow-hidden relative"
                    style={{ backgroundImage: `url('${blog.images[0]?.image}')` }}
                  >
                    <Link to={`/blog/${blog.slug}`}>
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <h3 className="title-small-6 text-[#FFFFFF]">{blog.title}</h3>
                        <p className="mt-2 title-small-7 text-color-8">{blog.short_description}</p>
                      </div>
                    </Link>
                  </div>
                ))}

                {blogData.slice(3, 4).map((blog, index) => (
                  <div
                    key={index}
                    className="h-[220px] md:h-[278px] bg-cover bg-center bg-no-repeat overflow-hidden relative md:col-span-2"
                    style={{ backgroundImage: `url('${blog.images[0]?.image}')` }}
                  >
                    <Link to={`/blog/${blog.slug}`}>
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <p className="title-small-8 text-[#FFFFFF]">{blog.title}</p>
                        <h3 className="body-xl-4 text-[#FFFFFF] mt-1">{blog.short_description}</h3>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Latest Blogs Section */}
      <div className='mt-[950px] md:mt-0 lg:mt-[100px]'>
        <div className="relative">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-20">
            <div className="flex flex-wrap justify-between items-center px-4 sm:px-8 md:px-1 py-4 gap-2">
              <h2 className="web-heading-section text-white">Latest Blogs</h2>
            </div>

            <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row gap-6">
              {/* Left Column (60%) */}
              <div className="flex flex-col md:flex-row md:gap-4 md:basis-[60%] w-full gap-4">
                {blogData.slice(4, 6).map((blog, index) => (
                  <div key={index} className="w-full md:w-1/2">
                    <Link to={`/blog/${blog.slug}`}>
                      <div
                        className="h-[400px] md:h-[570px] bg-cover bg-center bg-no-repeat overflow-hidden relative"
                        style={{ backgroundImage: `url('${blog.images[0]?.image}')` }}
                      >
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                          <h2 className="body-xl-3 text-[#FFFFFF] mb-2">{blog.title}</h2>
                          <p className="title-small-5 text-[#FFFFFF] mb-4">{blog.short_description}</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>

              {/* Right Column (40%) */}
              <div className="md:basis-[40%] grid grid-cols-1 md:grid-cols-2 gap-4">
                {blogData.slice(6, 8).map((blog, index) => (
                  <div
                    key={index}
                    className="h-[220px] md:h-[278px] bg-cover bg-center bg-no-repeat overflow-hidden relative md:col-span-2"
                    style={{ backgroundImage: `url('${blog.images[0]?.image}')` }}
                  >
                    <Link to={`/blog/${blog.slug}`}>
                      <div className="absolute bottom-0 left-0 right-0 p-4  text-white">
                        <p className="title-small-8 text-[#FFFFFF]">{blog.short_description}</p>
                        <h3 className="body-xl-4 text-[#FFFFFF] mt-1">{blog.title}</h3>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>


        </div>
      </div>

      {/* All Blogs Section */}
      {showAllBlogs && (
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-20">
          <h2 className="web-heading-section text-white mb-6">All Blogs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {blogData.slice(8).map((blog, index) => (
              <Link key={index} to={`/blog/${blog.slug}`}>
                <div className="h-[250px] bg-cover bg-center relative" style={{ backgroundImage: `url('${blog.images[0]?.image}')` }}>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <p className="title-small-5 text-[#FFFFFF] mb-4">{blog.short_description}</p>
                    <h2 className="body-xl-3 text-[#FFFFFF] mb-2">{blog.title}</h2>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
      <div className='flex justify-center items-center py-8'>
        {/* "See More" or "See All" Button */}
        {hasMore && (
          <DropdownButton
            onClick={() => {
              loadMoreBlogs();
              setShowAllBlogs(true); // Show All Blogs once the button is clicked
            }}
            disabled={!hasMore} // Disable the button when no more blogs are available
          >
            {showAllBlogs ? t("see_more") : t("see_all")}

          </DropdownButton>
        )}
      </div>

    </>
  );
};

export default BlogSection;